import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PortfolioProvider } from "./context/PortfolioContext";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Above-fold: load eagerly
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import SkillsSection from "./components/SkillsSection";
import ExperienceSection from "./components/ExperienceSection";

// Below-fold: lazy loaded
const ProjectsSection = lazy(() => import("./components/ProjectsSection"));
const ContactSection = lazy(() => import("./components/ContactSection"));
const Footer = lazy(() => import("./components/Footer"));

// Admin: lazy loaded (never needed by public visitors)
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));
const AdminPersonalInfo = lazy(() => import("./components/admin/AdminPersonalInfo"));
const AdminSkills = lazy(() => import("./components/admin/AdminSkills"));
const AdminExperience = lazy(() => import("./components/admin/AdminExperience"));
const AdminProjects = lazy(() => import("./components/admin/AdminProjects"));
const AdminEducation = lazy(() => import("./components/admin/AdminEducation"));
const AdminCertifications = lazy(() => import("./components/admin/AdminCertifications"));
const AdminMessages = lazy(() => import("./components/admin/AdminMessages"));
const AdminSettings = lazy(() => import("./components/admin/AdminSettings"));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

const Portfolio = () => {
  return (
    <div className="bg-[#fafbfc] min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <SkillsSection />
        <ExperienceSection />
        <Suspense fallback={null}>
          <ProjectsSection />
          <ContactSection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <Routes>
                        <Route path="/" element={<AdminDashboard />} />
                        <Route path="/dashboard" element={<AdminDashboard />} />
                        <Route path="/personal-info" element={<AdminPersonalInfo />} />
                        <Route path="/skills" element={<AdminSkills />} />
                        <Route path="/experience" element={<AdminExperience />} />
                        <Route path="/projects" element={<AdminProjects />} />
                        <Route path="/education" element={<AdminEducation />} />
                        <Route path="/certifications" element={<AdminCertifications />} />
                        <Route path="/messages" element={<AdminMessages />} />
                        <Route path="/settings" element={<AdminSettings />} />
                      </Routes>
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* Public Portfolio Routes */}
              <Route
                path="/"
                element={
                  <PortfolioProvider>
                    <Portfolio />
                  </PortfolioProvider>
                }
              />
              <Route
                path="*"
                element={
                  <PortfolioProvider>
                    <Portfolio />
                  </PortfolioProvider>
                }
              />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
